import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart3, Plus, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const statusOptions = ["not_started", "in_progress", "completed", "on_hold"];
const statusLabels: Record<string, string> = {
  not_started: "Not Started", in_progress: "In Progress", completed: "Completed", on_hold: "On Hold",
};

const AdminProgress = () => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("in_progress");
  const [progress, setProgress] = useState(0);

  const { data: companies = [] } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => {
      const { data } = await supabase.from("companies").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ["admin-updates"],
    queryFn: async () => {
      const { data } = await supabase
        .from("project_updates")
        .select("*, companies(name)")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const resetForm = () => {
    setTitle(""); setContent(""); setStatus("in_progress"); setProgress(0);
    setSelectedCompany(""); setEditId(null); setShowForm(false);
  };

  const saveUpdate = useMutation({
    mutationFn: async () => {
      const payload = {
        company_id: selectedCompany,
        title: title.trim(),
        content: content.trim() || null,
        status,
        progress_percent: progress,
      };
      if (editId) {
        const { error } = await supabase.from("project_updates").update(payload).eq("id", editId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("project_updates").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      resetForm();
      qc.invalidateQueries({ queryKey: ["admin-updates"] });
      toast.success(editId ? "Update saved" : "Update posted");
    },
    onError: () => toast.error("Failed to save"),
  });

  const deleteUpdate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("project_updates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-updates"] });
      toast.success("Update deleted");
    },
    onError: () => toast.error("Failed to delete"),
  });

  const startEdit = (u: typeof updates[0]) => {
    setEditId(u.id);
    setSelectedCompany(u.company_id);
    setTitle(u.title);
    setContent(u.content ?? "");
    setStatus(u.status ?? "in_progress");
    setProgress(u.progress_percent ?? 0);
    setShowForm(true);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
          <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
            Progress <span className="accent-text">Updates</span>
          </h1>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Cancel" : "New Update"}
        </button>
      </div>

      {showForm && (
        <div className="glass-card rounded-2xl p-6 mb-6">
          <h2 className="font-display text-lg text-primary-foreground mb-4">{editId ? "Edit Update" : "Post Update"}</h2>
          <form onSubmit={(e) => { e.preventDefault(); saveUpdate.mutate(); }} className="space-y-4">
            <select
              value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} required
              className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm focus:outline-none focus:border-teal/40 transition-all"
            >
              <option value="">Select company...</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" required
              className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all"
            />
            <textarea
              value={content} onChange={(e) => setContent(e.target.value)} placeholder="Details (optional)" rows={3}
              className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all resize-none"
            />
            <div className="grid grid-cols-2 gap-4">
              <select
                value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm focus:outline-none focus:border-teal/40 transition-all"
              >
                {statusOptions.map((s) => <option key={s} value={s}>{statusLabels[s]}</option>)}
              </select>
              <div>
                <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/40 mb-1.5 font-body">
                  Progress: {progress}%
                </label>
                <input
                  type="range" min={0} max={100} value={progress} onChange={(e) => setProgress(Number(e.target.value))}
                  className="w-full accent-teal"
                />
              </div>
            </div>
            <button
              type="submit" disabled={saveUpdate.isPending}
              className="px-6 py-3 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body disabled:opacity-50"
            >
              {saveUpdate.isPending ? "Saving..." : editId ? "Save Changes" : "Post Update"}
            </button>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : updates.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <BarChart3 className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No updates yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {updates.map((u) => (
            <div key={u.id} className="glass-card rounded-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg text-primary-foreground font-display">{u.title}</h3>
                  <p className="text-xs text-primary-foreground/30 font-body mt-0.5">
                    {(u.companies as { name: string } | null)?.name ?? "—"} · {new Date(u.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(u)} className="p-2 rounded-lg text-primary-foreground/20 hover:text-teal hover:bg-teal/5 transition-all">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { if (confirm(`Delete "${u.title}"?`)) deleteUpdate.mutate(u.id); }}
                    className="p-2 rounded-lg text-primary-foreground/20 hover:text-destructive hover:bg-destructive/5 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-primary-foreground/40 font-body">{statusLabels[u.status ?? "in_progress"]}</span>
                <span className="text-xs text-teal font-body">{u.progress_percent}%</span>
              </div>
              <div className="w-full h-2 bg-primary-foreground/5 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-gradient-to-r from-teal to-lime rounded-full" style={{ width: `${u.progress_percent}%` }} />
              </div>
              {u.content && <p className="text-sm text-primary-foreground/50 font-body whitespace-pre-wrap">{u.content}</p>}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProgress;
